import { useAuthenticatedFetch, useAppBridge } from '@shopify/app-bridge-react';
import { useSearchParams } from 'react-router-dom';

// Hook to get shop parameter from URL
export function useShop() {
  const [searchParams] = useSearchParams();
  return searchParams.get('shop') || '';
}

// Helper to add shop parameter to API URLs
export function addShopParam(url: string, shop: string): string {
  if (!shop) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}shop=${encodeURIComponent(shop)}`;
}

async function parseErrorMessage(response: Response): Promise<string> {
  try {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const data: any = await response.json();
      return data?.error?.message || data?.error || data?.message || response.statusText || `Request failed (${response.status})`;
    }
    const text = await response.text();
    return text || response.statusText || `Request failed (${response.status})`;
  } catch {
    return response.statusText || `Request failed (${response.status})`;
  }
}

// Track redirect attempts to prevent infinite loops
let redirectAttempted = false;
let redirectTimeout: ReturnType<typeof setTimeout> | null = null;

function redirectToInstallIfNeeded(message: string, _appBridge?: unknown) {
  // If we don't have a session in Mongo yet, kick off OAuth install automatically.
  if (!/no session found|session expired/i.test(message)) return;

  // Prevent infinite redirect loops
  if (redirectAttempted) {
    console.warn('Redirect already attempted, skipping to prevent loop');
    // Instead of doing nothing, redirect to install page
    const params = new URLSearchParams(window.location.search);
    const shop = params.get('shop') || '';
    if (shop && !window.location.pathname.includes('/install')) {
      window.location.href = `/install?shop=${encodeURIComponent(shop)}&${params.toString()}`;
    }
    return;
  }

  // Check if we're already on an auth or install page
  if (window.location.href.includes('/api/auth') || window.location.pathname.includes('/install')) {
    console.warn('Already on auth/install page, skipping redirect');
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const shop = params.get('shop') || '';
  const host = params.get('host') || '';

  if (!shop) {
    console.warn('No shop parameter found, redirecting to install page');
    window.location.href = '/install';
    return;
  }

  redirectAttempted = true;

  // Redirect to install page instead of directly to OAuth
  // This gives better UX and allows manual retry
  const installUrl = `/install?shop=${encodeURIComponent(shop)}${host ? `&host=${encodeURIComponent(host)}` : ''}`;
  console.log('Redirecting to install page:', installUrl);
  window.location.href = installUrl;

  // Reset redirect flag after 5 seconds to allow retry if needed
  if (redirectTimeout) clearTimeout(redirectTimeout);
  redirectTimeout = setTimeout(() => {
    redirectAttempted = false;
  }, 5000);
}

export class ApiError extends Error {
  status: number;
  code?: string;
  reinstallUrl?: string;

  constructor(message: string, status: number, code?: string, reinstallUrl?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.reinstallUrl = reinstallUrl;
  }
}

async function handleResponse<T>(response: Response, appBridge?: any): Promise<T> {
  if (!response.ok) {
    // Clone BEFORE reading the body — reading is destructive; the clone
    // preserves the original stream so we can parse it twice.
    const cloned = response.clone();

    const message = await parseErrorMessage(response);

    // Parse structured error code and reinstallUrl from the cloned response
    let code: string | undefined;
    let reinstallUrl: string | undefined;
    try {
      const errBody: any = await cloned.json().catch(() => null);
      code = errBody?.code;
      reinstallUrl = errBody?.reinstallUrl;
    } catch { /* ignore */ }

    if (response.status === 401 || code === 'TOKEN_REVOKED') {
      // If token is revoked, break out of the Shopify Admin iframe
      // and redirect the top-level window to the OAuth reinstall page.
      if (code === 'TOKEN_REVOKED' && reinstallUrl) {
        const fullUrl = reinstallUrl.startsWith('http')
          ? reinstallUrl
          : `https://apilabely.esire.org${reinstallUrl}`;
        console.warn('TOKEN_REVOKED — redirecting top window to:', fullUrl);
        try {
          (window.top || window).location.href = fullUrl;
        } catch {
          window.location.href = fullUrl;
        }
      } else {
        redirectToInstallIfNeeded(message, appBridge);
      }
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    throw new ApiError(message, response.status, code, reinstallUrl);
  }
  // Some endpoints may return empty bodies (204)
  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return (await response.json()) as T;
  }

  // Fallback: try text
  return (await response.text()) as unknown as T;
}

// Create authenticated API client
export function useApiClient() {
  const authenticatedFetch = useAuthenticatedFetch();
  const appBridge = useAppBridge();
  const shop = useShop();

  return {
    get: async <T = any>(endpoint: string): Promise<T> => {
      const url = addShopParam(endpoint, shop);
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response) throw new Error('No response');
      return handleResponse<T>(response, appBridge);
    },
    post: async <T = any>(endpoint: string, data: any): Promise<T> => {
      const url = addShopParam(endpoint, shop);
      const response = await authenticatedFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!response) throw new Error('No response');
      return handleResponse<T>(response, appBridge);
    },
    put: async <T = any>(endpoint: string, data: any): Promise<T> => {
      const url = addShopParam(endpoint, shop);
      const response = await authenticatedFetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!response) throw new Error('No response');
      return handleResponse<T>(response, appBridge);
    },
    delete: async <T = any>(endpoint: string): Promise<T> => {
      const url = addShopParam(endpoint, shop);
      const response = await authenticatedFetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response) throw new Error('No response');
      return handleResponse<T>(response, appBridge);
    },
    patch: async <T = any>(endpoint: string, data: any): Promise<T> => {
      const url = addShopParam(endpoint, shop);
      const response = await authenticatedFetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!response) throw new Error('No response');
      return handleResponse<T>(response, appBridge);
    },
  };
}
